import _ from "lodash";
import React, { Component } from "react";
import { withRouter } from "react-router-dom";

import { api } from "@/APIRequests";
import {
  ErrorCard,
  PatientChartPaymentBanner,
  Text,
  TextColors,
  TextTypes,
} from "@/component-library";
import { isNoteNoShow } from "@/components/Common/GuidedVideoCall/Steps/ProviderVideoWorkflowStep";
import { PAYMENT_TAB } from "@/constants/MemberProfile";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import dayjs from "@/lib/helpers/dayjs";
import { isUserAdmin } from "@/redux/actions/auth";
import { hasAccessToPaymentTab } from "@/utils/authUtils";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { getQueryParams } from "@/utils/filters";

import MemberProfileAdmin from "./MemberProfileAdmin";
import MemberProfileCarePlan from "./MemberProfileCarePlan";
import MemberProfileHeader from "./MemberProfileHeader";
import MemberProfileMedical from "./MemberProfileMedical";
import MemberProfileNotes from "./MemberProfileNotes";
import MemberProfileTimeline from "./MemberProfileTimeline";
import { Payment } from "./Payment/Payment";

import "../styles.scss";

import { MemberProfileReports } from "./Reports/MemberProfileReports";

export const _90_DAY_CARE_PLAN = "90_day_care_plan";

class MemberProfile extends Component {
  buildInitialState() {
    let selectedTab = getQueryParams("selectedTab", this.props.location.search);

    if (!selectedTab) {
      selectedTab = "Care Plan";
    }

    return {
      carePlan: null,
      carePlanData: {
        carePlanFile: null,
        careTeam: null,
      },
      eligibilityDetails: {},
      files: [],
      isCarePlanLoaded: false,
      isPatientDetailsLoaded: false,
      medicalData: {
        insuranceImageUrls: {
          pbUrl: null,
          pfUrl: null,
          sbUrl: null,
          sfUrl: null,
        },
      },
      noteData: null,
      patientDetails: {
        a5_screening_id: null,
        address: {
          city: null,
          state: null,
          street: null,
          zip_code: null,
        },
        assessment_status: {
          assessment_status: false,
          initial_prescriber_call: false,
          initial_video_consult: false,
          schedule_video_call: false,
        },
        dob: null,
        email: null,
        emergency_contact_number: null,
        emergency_contact_person: null,
        first_name: null,
        gender: null,
        initial_clinical_assessment_complete: false,
        is_scheduling_restricted: null,
        last_name: null,
        mobile: null,
        ninety_day_care: null,
        outcome_clinical_assessment_complete: false,
        post_90_day_rating_scale: null,
        primary_insurance: {
          carrier: null,
          member_id: null,
          policy_dob: null,
          policy_holder_name: null,
          policy_relationship: null,
          state: null,
        },
        rating_scale: null,
        referral_id: null,
        referred_date: null,
        reports: {},
        secondary_insurance: {
          carrier: null,
          member_id: null,
          policy_dob: null,
          policy_holder_name: null,
          policy_relationship: null,
          state: null,
        },
        status: null,
      },
      patientDiagnosis: null,
      patientId: this.props.match.params.patientId,
      patientName: "",
      progressTrendsData: {},
      selectedTab: selectedTab,
      timelineData: {
        activities: [],
        practiceName: null,
      },
      unfilteredNotes: null,
    };
  }

  constructor(props) {
    super(props);
    this.adminFocusRef = React.createRef();
    this.isAdmin = isUserAdmin();
    this.state = this.buildInitialState();
  }

  async componentDidMount() {
    const patientDetails = await this.getPatientDetailsAndFiles();

    await this.getCarePlan();
    const urlParams = { patientId: this.props.match.params.patientId };

    this.setState({
      patientDiagnosis: await api.shared.get_patient_diagnosis({ urlParams }),
    });

    if (this.state.selectedTab === "Notes") {
      await this.getProgressNotes(); // Populates state.noteData (filtered by note type)
    }

    if (this.state.selectedTab === "Medical") {
      await this.getProgressNotes(); // Populates state.unfilteredNotes (all notes, sorted)

      if (this.isAdmin) {
        await this.getEligibilityDetails(patientDetails);
      }
    }
  }

  async componentDidUpdate(prevProps, prevState) {
    // Explicitly reset all state on patientId change to minimize risk of leftover state as multiple API calls
    // are involved. An explicit return is used (rather than using the shared 'patientIdChange' function) to prevent
    // a race condition with the other tab-switching behavior
    if (prevProps.match.params.patientId !== this.props.match.params.patientId) {
      this.setState(this.buildInitialState(), () => {
        this.componentDidMount();
      });

      return;
    }

    if (prevState.selectedTab !== this.state.selectedTab && this.state.selectedTab === "Notes") {
      await this.getProgressNotes(); // Populates state.noteData (filtered by note type)
    }

    if (prevState.selectedTab !== this.state.selectedTab && this.state.selectedTab === "Medical") {
      await this.getProgressNotes(); // Populates state.unfilteredNotes (all notes, sorted)

      if (this.isAdmin) {
        await this.getEligibilityDetails();
      }
    }
  }

  async getCarePlan() {
    const options = { params: { showLoader: true } };
    const urlParams = { patientId: this.state.patientId };
    const response = await api.shared.fetch_patient_care_plan_v2({ options, urlParams });

    this.setState({
      carePlan: response,
      isCarePlanLoaded: true,
    });
  }

  async getProgressNotes() {
    const urlParams = { patientId: this.state.patientId };
    const response = await api.shared.fetch_patient_notes_summary({ urlParams });

    if (response) {
      const notes = sortNotesBySessionDate(response);

      this.setState({
        noteData: filterNotesByNoteType(notes),
        unfilteredNotes: notes,
      });
    }
  }

  async getPatientDetails() {
    const urlParams = { patientId: this.state.patientId };
    const options = { params: { showLoader: true } };
    const response = await api.shared.fetch_patient_details({ options, urlParams });
    const patientDetails = response.user;
    const patientName = `${patientDetails.first_name} ${patientDetails.last_name}`;
    const eligibilityDetails = patientDetails.primary_eligibility_record;

    this.setState({
      eligibilityDetails: eligibilityDetails,
      isPatientDetailsLoaded: true,
      patientDetails: patientDetails,
      patientName: patientName,
    });

    return patientDetails;
  }

  async getEligibilityDetails(patientDetailsParam) {
    const patientDetails = patientDetailsParam || this.state.patientDetails;
    const eligibilityRecordId = patientDetails?.primary_eligibility_record?.id;

    if (!eligibilityRecordId) {
      return;
    }

    try {
      const data = {
        id: eligibilityRecordId,
        query_type: "id",
      };

      const eligibilityResponse = await api.admin.fetch_insurance_eligibility_records({ data });

      if (eligibilityResponse) {
        this.setState({ eligibilityDetails: eligibilityResponse });
      }
    } catch (error) {
      captureErrorMessage(parseError({ error, fallback: "Error fetching eligibility details" }), {
        tags: {
          action: "getEligibilityDetails",
          component: "MemberProfile",
          errorType: "query",
          patientId: this.state.patientId,
        },
      });
    }
  }

  async getActivities() {
    let urlParams = { patientId: this.state.patientId };
    const options = { params: { showLoader: true } };

    try {
      const response = await api.shared.fetch_patient_activity_feed({ options, urlParams });

      this.setState({
        timelineData: {
          ...this.state.timelineData,
          activities: response.results,
        },
      });
    } catch (error) {
      console.log(error);
    }
  }

  async getPatientDetailsAndFiles() {
    const patientDetails = await this.getPatientDetails();
    const files = await api.shared.get_files({
      entity_id: this.state.patientId,
      entity_type: "patient",
    });

    this.setState({
      files: files,
    });

    return patientDetails;
  }

  getAssociatedFiles(files) {
    return files.filter((file) => {
      if (file?.file_sub_type === "ASSOCIATED_FILE_PATIENT") {
        return file;
      }
    });
  }

  renderCarePlanLoading = () => {
    // Stop gap until sticky footers are added
    return <div style={{ height: "100px" }} />;
  };

  render() {
    const showRestrictedSchedulingBanner = this?.state?.patientDetails?.is_scheduling_restricted;

    const selectedTabChangeHandler = (tab) => {
      const searchParams = new URLSearchParams(this.props.history.location.search);

      searchParams.set("selectedTab", tab);

      const newTabPath = `${this.props.history.location.pathname}?${searchParams.toString()}`;

      this.props.history.push(newTabPath);
      this.setState({ selectedTab: tab });
    };

    const renderSchedulingRestrictedBannerText = () => {
      const adminTextSuffix = (
        <span
          className="bold apti-green-v2"
          onClick={() => {
            this.setState({ selectedTab: "Admin" }, () =>
              this?.adminFocusRef?.current?.scrollIntoView(),
            );
          }}
        >
          Change Scheduling Restrictions
        </span>
      );

      const nonAdminTextSuffix = "To remove this restriction, please contact your " + "supervisor.";

      const textSuffix = this.isAdmin ? adminTextSuffix : nonAdminTextSuffix;

      return <>This patient is unable to schedule or reschedule appointments. {textSuffix}</>;
    };

    const paywallStatus = this.state.patientDetails?.paywall_status;
    const patientName =
      this.state.patientDetails?.preferred_name || this.state.patientDetails.first_name;
    const automatic_payment_enabled = this.state.patientDetails?.automatic_payment_enabled;

    return (
      <div {...getAutomationDataAttr("MemberProfilePage")}>
        {this.state.isPatientDetailsLoaded && (
          <>
            {paywallStatus?.paywall && automatic_payment_enabled && (
              <PatientChartPaymentBanner
                lastFailedTransactionTimestamp={paywallStatus?.last_failed_transaction_date}
                patientName={patientName}
                paywallType={paywallStatus.type}
                softPaywallTimestamp={this.state.patientDetails?.soft_paywall}
              />
            )}

            {!paywallStatus?.paywall && showRestrictedSchedulingBanner && (
              <ErrorCard
                className="MemberProfile__error"
                {...getAutomationDataAttr("patient-profile-scheduling-restricted-banner")}
              >
                <Text color={TextColors.red} type={TextTypes.label}>
                  Patient Scheduling Restricted
                </Text>

                <Text color={TextColors.black} type={TextTypes.paragraph}>
                  {renderSchedulingRestrictedBannerText()}
                </Text>
              </ErrorCard>
            )}

            <MemberProfileHeader
              eligibilityDetails={this.state.eligibilityDetails}
              patientDetails={this.state.patientDetails}
              patientDiagnosis={this.state.patientDiagnosis}
              // used to refetch data after a user's status is changed
              refetchDetails={async () => {
                await this.getPatientDetails();
                await this.getActivities();
              }}
              selectedTab={this.state.selectedTab}
              selectedTabChangeHandler={selectedTabChangeHandler}
              setPatientStatus={(status) => {
                const patientDetails = { ...this.state.patientDetails };

                patientDetails.status = status;
                this.setState({ patientDetails });
              }}
              updateState={(newState) => {
                this.setState(newState);
              }}
            />

            {this.state.selectedTab === "Reports" && (
              <MemberProfileReports data={this.state.files} />
            )}

            {this.state.selectedTab === "Care Plan" && this.state.isCarePlanLoaded ? (
              <MemberProfileCarePlan
                carePlan={this.state.carePlan}
                data={this.state.carePlanData}
                patientDetails={this.state.patientDetails}
                updateState={(newState) => this.setState(newState)}
              />
            ) : (
              this.state.selectedTab === "Care Plan" && this.renderCarePlanLoading()
            )}

            {this.state.selectedTab === "Notes" && (
              <MemberProfileNotes
                data={this.state.noteData}
                patientDetails={this.state.patientDetails}
                patientDiagnosis={this.state.patientDiagnosis}
                updateState={(newState) => this.setState(newState)}
              />
            )}

            {this.state.selectedTab === "Medical" && (
              <MemberProfileMedical
                data={this.state.medicalData}
                eligibilityDetails={this.state.eligibilityDetails}
                files={this.getAssociatedFiles(this.state.files)}
                noteData={this.state.unfilteredNotes}
                patientDetails={this.state.patientDetails}
                updateState={(newState) => {
                  const updatedState = {
                    ...newState,
                    files: [..._.get(newState, "files", []), ...this.state.files],
                  };

                  this.setState(updatedState);
                }}
              />
            )}

            {this.state.selectedTab === "Timeline" && (
              <MemberProfileTimeline
                data={this.state.timelineData}
                patientDetails={this.state.patientDetails}
                updateState={(newState) => this.setState(newState)}
              />
            )}

            {hasAccessToPaymentTab() && this.state.selectedTab === PAYMENT_TAB && (
              <Payment patientDetails={this.state.patientDetails} />
            )}

            {this.isAdmin && this.state.selectedTab === "Admin" && (
              <MemberProfileAdmin
                adminFocusRef={this.adminFocusRef}
                patientDetails={this.state.patientDetails}
                refetchDetails={async () => {
                  await this.getPatientDetails();
                  await this.getActivities();
                }}
                selectedTabChangeHandler={selectedTabChangeHandler}
                timelineData={this.state.timelineData}
                updateState={(newState) => this.setState(newState)}
              />
            )}
          </>
        )}
      </div>
    );
  }
}

export function filterNotesByCompletedAndNotNoShow(notes) {
  return notes.filter((note) => note.completed_date_time && !isNoteNoShow(note));
}

export function filterNotesByNoteType(notes) {
  return notes.filter(
    (note) =>
      note.note_type == null ||
      note.note_type === "Video Consultation" ||
      note.note_type === "Check-in" ||
      note.note_type === "Care Coordination" ||
      note.note_type === "Initial Clinical Assessment" ||
      note.note_type === "Care Communication" ||
      note.note_type === "Care Management" ||
      note.note_type === "Case Management" ||
      note.note_type === "Advanced Care Activity",
  );
}

export function patientIdChange(prevProps) {
  if (prevProps.match.params.patientId !== this.props.match.params.patientId) {
    this.setState({ patientId: this.props.match.params.patientId }, () => this.componentDidMount());
  }
}

// Sorts notes by session date in descending order (newest first). Creates a shallow copy to
// avoid mutating the original array. Falls back to 00:00 for missing session_from times.
// This mimics original moment behavior
export function sortNotesBySessionDate(notes) {
  return [...notes].sort((a, b) => {
    const sessionFromA = a.session_from || "00:00";
    const sessionFromB = b.session_from || "00:00";

    const dateA = dayjs(`${a.session_date} ${sessionFromA}`, "MM/DD/YYYY HH:mm");
    const dateB = dayjs(`${b.session_date} ${sessionFromB}`, "MM/DD/YYYY HH:mm");

    // Push notes with invalid/missing session_date to end of list to prevent NaN comparisons
    // from causing unstable sort order. Invalid dates likely indicate incomplete data entry
    // and are less important than recent valid notes in descending chronological order.
    if (!dateA.isValid()) return 1;
    if (!dateB.isValid()) return -1;

    return dateB.valueOf() - dateA.valueOf();
  });
}
export default withRouter(MemberProfile);
